import React, { useState } from 'react';
import { View, Text, StyleSheet, TouchableOpacity, TextInput, Switch } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { colors, spacing, typography } from '../theme';
import { useLocale } from '../contexts/LocaleContext';
import { useThemedStyles } from '../hooks/useThemedStyles';

export interface VisitLocationOverrideValue {
  enabled?: boolean;
  radiusMeters?: number;
  maxAccuracyMeters?: number;
  allowOverrideWithReason?: boolean;
}

export interface VisitPhotoOverrideValue {
  requireOnStart?: boolean;
  requireOnEnd?: boolean;
  minPhotosOnStart?: number;
  minPhotosOnEnd?: number;
  maxPhotos?: number;
}

export interface VisitCalendarOverrideValue {
  autoCreateEvent?: boolean;
}

export interface VisitOverrideValues {
  location?: VisitLocationOverrideValue;
  photos?: VisitPhotoOverrideValue;
  calendar?: VisitCalendarOverrideValue;
}

interface VisitOverrideEditorProps {
  value: VisitOverrideValues;
  onChange: (value: VisitOverrideValues) => void;
  /** Collapsed by default — most visits/campaigns should just inherit the
   * parent config, so this stays out of the way until opted into. */
  defaultExpanded?: boolean;
}

/**
 * Full location/photo/calendar override editor — shared across VisitCreateScreen,
 * VisitCampaignCreateScreen, and VisitCampaignDetailScreen's post-creation edit
 * (and available to VisitDetailScreen if a per-visit post-creation edit is added
 * there too), so the three "set overrides" surfaces don't each hand-roll their
 * own partial subset of fields. Exposes every field on VisitLocationEnforcement/
 * VisitPhotoEnforcement/VisitCalendarDefaults EXCEPT `cameraOnly` (a constant
 * per product decision, never actually configurable) and `defaultCalendarId`/
 * `reminderOffsets` (calendar integration specifics not yet exposed anywhere
 * else in the app either) — see visit-service.entity.ts for the full shape.
 *
 * `value`/`onChange` carry ONLY the fields the caller has actually chosen to
 * override — an omitted field means "inherit from campaign/service", matching
 * the backend's own Partial<...> override semantics (VisitConfigResolverService.
 * resolveEffectiveEnforcement replaces a field only when the override object
 * defines it, never a blanket replace of the whole group).
 */
export const VisitOverrideEditor: React.FC<VisitOverrideEditorProps> = ({ value, onChange, defaultExpanded = false }) => {
  const styles = useThemedStyles(getStyles);
  const { t } = useLocale();
  const [expanded, setExpanded] = useState(defaultExpanded);

  const location = value.location ?? {};
  const photos = value.photos ?? {};
  const calendar = value.calendar ?? {};

  const patchLocation = (patch: Partial<VisitLocationOverrideValue>) => {
    onChange({ ...value, location: { ...location, ...patch } });
  };
  const patchPhotos = (patch: Partial<VisitPhotoOverrideValue>) => {
    onChange({ ...value, photos: { ...photos, ...patch } });
  };
  const patchCalendar = (patch: Partial<VisitCalendarOverrideValue>) => {
    onChange({ ...value, calendar: { ...calendar, ...patch } });
  };

  const numberField = (
    label: string,
    fieldValue: number | undefined,
    onSet: (n: number | undefined) => void,
    placeholder: string
  ) => (
    <View style={styles.fieldRow}>
      <Text style={styles.fieldLabel}>{label}</Text>
      <TextInput
        style={styles.numberInput}
        keyboardType="number-pad"
        value={fieldValue != null ? String(fieldValue) : ''}
        onChangeText={(text) => onSet(text.trim() ? Number(text) || undefined : undefined)}
        placeholder={placeholder}
        placeholderTextColor={colors.text.secondary as string}
      />
    </View>
  );

  const switchField = (
    label: string,
    fieldValue: boolean | undefined,
    onSet: (v: boolean) => void
  ) => (
    <View style={styles.fieldRow}>
      <Text style={styles.fieldLabel}>{label}</Text>
      <Switch
        value={!!fieldValue}
        onValueChange={onSet}
        trackColor={{ true: colors.primary as string, false: colors.border as string }}
        thumbColor={colors.white as string}
      />
    </View>
  );

  return (
    <View style={styles.container}>
      <TouchableOpacity style={styles.toggleRow} onPress={() => setExpanded((v) => !v)}>
        <Text style={styles.sectionTitle}>{t('visitOverride.title')}</Text>
        <Ionicons name={expanded ? 'chevron-up' : 'chevron-down'} size={18} color={colors.text.secondary as string} />
      </TouchableOpacity>

      {expanded && (
        <>
          <Text style={styles.groupTitle}>{t('visitOverride.locationGroup')}</Text>
          {switchField(t('visitOverride.locationEnabled'), location.enabled, (v) => patchLocation({ enabled: v }))}
          {numberField(t('visitOverride.radiusMeters'), location.radiusMeters, (n) => patchLocation({ radiusMeters: n }), t('visitOverride.inheritPlaceholder'))}
          {numberField(t('visitOverride.maxAccuracyMeters'), location.maxAccuracyMeters, (n) => patchLocation({ maxAccuracyMeters: n }), t('visitOverride.inheritPlaceholder'))}
          {switchField(t('visitOverride.allowOverrideWithReason'), location.allowOverrideWithReason, (v) => patchLocation({ allowOverrideWithReason: v }))}

          <Text style={styles.groupTitle}>{t('visitOverride.photosGroup')}</Text>
          {switchField(t('visitOverride.requireOnStart'), photos.requireOnStart, (v) => patchPhotos({ requireOnStart: v }))}
          {photos.requireOnStart && numberField(t('visitOverride.minPhotosOnStart'), photos.minPhotosOnStart, (n) => patchPhotos({ minPhotosOnStart: n }), '1')}
          {switchField(t('visitOverride.requireOnEnd'), photos.requireOnEnd, (v) => patchPhotos({ requireOnEnd: v }))}
          {photos.requireOnEnd && numberField(t('visitOverride.minPhotosOnEnd'), photos.minPhotosOnEnd, (n) => patchPhotos({ minPhotosOnEnd: n }), '1')}
          {numberField(t('visitOverride.maxPhotos'), photos.maxPhotos, (n) => patchPhotos({ maxPhotos: n }), t('visitOverride.inheritPlaceholder'))}

          <Text style={styles.groupTitle}>{t('visitOverride.calendarGroup')}</Text>
          {switchField(t('visitOverride.autoCreateEvent'), calendar.autoCreateEvent, (v) => patchCalendar({ autoCreateEvent: v }))}
        </>
      )}
    </View>
  );
};

/** Strips undefined-valued fields from an override group so an empty {}
 * (no field actually set) is sent as `undefined` instead — the backend
 * treats "override group present at all" as "replace this group", so an
 * accidentally-empty {} would still (harmlessly, but pointlessly) replace
 * nothing meaningful; cleaner to just omit the group entirely. */
export function cleanOverrideGroup<T extends Record<string, any>>(group: T | undefined): T | undefined {
  if (!group) return undefined;
  const entries = Object.entries(group).filter(([, v]) => v !== undefined);
  return entries.length > 0 ? (Object.fromEntries(entries) as T) : undefined;
}

const getStyles = () => StyleSheet.create({
  container: { marginTop: spacing.sm },
  toggleRow: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' },
  sectionTitle: {
    fontSize: typography.fontSize.base,
    fontFamily: typography.fontFamily.semibold,
    color: colors.text.primary as string,
  },
  groupTitle: {
    fontSize: typography.fontSize.sm,
    fontFamily: typography.fontFamily.semibold,
    color: colors.text.secondary as string,
    textTransform: 'uppercase',
    marginTop: spacing.md,
    marginBottom: spacing.xs,
  },
  fieldRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingVertical: spacing.xs,
  },
  fieldLabel: {
    flex: 1,
    fontSize: typography.fontSize.sm,
    fontFamily: typography.fontFamily.regular,
    color: colors.text.primary as string,
    marginRight: spacing.sm,
  },
  numberInput: {
    width: 90,
    borderWidth: 1,
    borderColor: colors.border as string,
    borderRadius: 8,
    paddingVertical: spacing.xs,
    paddingHorizontal: spacing.sm,
    fontSize: typography.fontSize.sm,
    fontFamily: typography.fontFamily.regular,
    color: colors.text.primary as string,
    textAlign: 'right',
  },
});
